<template>
  <div class="multiplier-card">
    <span
      v-if="formattedName"
      class="multiplier-card-name"
    >
      {{ formattedName }}
    </span>

    <canvas
      class="multiplier-card-canvas"
      ref="canvas"
      :width="width"
      :height="height"
    />

    <qrcode-vue
      v-if="url"
      class="multiplier-card-qrcode"
      ref="qrcodeVue"
      level="H"
      :render-as="'canvas'"
      :value="url"
      :size="300"
    />
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue'
import themes, { layouts as bgImages } from '@/constants/multipliers'

export default {
  name: "MultiplierCard",

  components: {
    QrcodeVue
  },

  props: {
    name: {
      type: String,
      required: true
    },
    card: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      layout: null,
      plan: null,
      planCode: null,
      url: null,
      theme: null,
      tstImg: null
    }
  },

  computed: {
    context () {
      if (this.$refs.canvas) {
        return this.$refs.canvas.getContext('2d')
      }
      return null
    },
    formattedName () {
      const pieces = this.name.toLowerCase().split(' ').filter((p) => {
        if (p) {
          const chkDot = p.length === 2 && p.indexOf('.') > 0
          const chkArticle = p.length === 3 && p.indexOf('d') > 0
          const chkLength = p.length > 3

          return chkDot || chkArticle || chkLength
        }
      })

      return pieces.map((p, i) => (
        (i === 0 || i === pieces.length - 1) ? p : `${p[0]}.`
      ))
        .join(' ')
        .toUpperCase()
    }
  },

  setup () {
    return {
      width: 1004,
      height: 591,
      qrcode: 300,
      padding: 28,
      border: 10,
      bgImages,
    }
  },

  created () {
    this.fillCardData(this.card)
  },
  
  async mounted () {
    this.renderCard()
  },
  
  methods: {
    fillCardData (card) {
      if (card) {
        this.layout = card.layout.toLowerCase()
        this.plan = card.name
        this.planCode = card.code
        this.url = card.url
  
        this.theme = themes.find((cfg) => cfg.theme === this.layout)
        
        if (!this.theme) {
          this.theme = themes.find((cfg) => cfg.theme === 'default')
        }
      }
    },

    async renderCard () {
      this.context.font = this.theme.font
      
      await this.setBackgroundColor("#FFFFFF")
      await this.setBackground()
    },

    async setBackgroundColor (color) {
      this.context.fillStyle = color
      
      await this.context.fillRect(0, 0, this.width, this.height)
    },

    async setBackground () {
      this.setBackgroundColor(this.theme.background)

      const img = new Image()
      img.src = this.bgImages[this.layout]

      img.onload = await (async () => {
        this.context.drawImage(img, 0, 0, this.width, this.height)

        setTimeout(async () => {
          await this.setName()
          await this.setPlanName()
          await this.setQrcode()
        },100)
      })
    },

    async setName () {
      this.context.textAlign = this.theme.textAlign
      this.context.fillStyle = this.theme.color

      const posX = this.theme.textAlign === 'left'
        ? this.padding
        : this.width - this.padding
        
      await this.context.fillText(this.formattedName, posX, 55)
    },

    async setPlanName () {
      this.context.textAlign = this.theme.textAlign
      this.context.fillStyle = this.theme.color

      const posY = this.height - (this.border * 2)
      const posX = this.theme.textAlign === 'left'
        ? this.padding
        : this.width - this.padding
        
      await this.context.fillText(this.plan, posX, posY)
    },

    async setQrcode () {
      const canvas = this.$refs.qrcodeVue.$el.querySelector('canvas')
      const img = new Image()
      img.src = await canvas.toDataURL("image/jpeg")
      
      const rectY = 95
      const rectX = this.theme.textAlign === 'left'
        ? this.padding
        : this.width - this.padding - this.qrcode

      this.context.fillStyle = "#FFFFFF"
      await this.context.fillRect(rectX, rectY, this.qrcode, this.qrcode)

      const codY = rectY + this.border
      const codX = rectX + this.border
      const codSize = this.qrcode - (this.border * 2)

      await this.context.drawImage(img, codX, codY, codSize, codSize)
    }
  },
  watch: {
    async card(nCard) {
      await this.fillCardData(nCard)
      await this.renderCard()
    }
  }
}
</script>

<style lang="scss" scoped>
.multiplier-card {
  display: flex;
  flex-direction: column;
  justify-content: center;

  &-canvas {
    max-width: 100%;
  }

  &-name {
    font-family: Open Sans;
    display: none;
  }

  &-qrcode {
    display: none;
  }
}
</style>
